import { Component, HostBinding, OnDestroy, Renderer2, ElementRef, input, effect } from '@angular/core';

import { TabDirective } from './tab.directive';
import { TabsetConfig } from './tabset.config';
import { NgTranscludeDirective } from './ng-transclude.directive';
import { NgClass } from '@angular/common';
// todo: add active event to tab
// todo: fix? mixing static and dynamic tabs position tabs in order of creation
@Component({
    selector: 'tabset',
    templateUrl: './tabset.component.html',
    styleUrls: ['./tabs.scss'],
    standalone: true,
    imports: [NgClass, NgTranscludeDirective]
})
export class TabsetComponent implements OnDestroy {
  /** if true tabs will be placed vertically */
  vertical = input<boolean>(false);

  /** if true tabs fill the container and have a consistent width */
  justified = input<boolean>(false);

  /** navigation context class: 'tabs' or 'pills' */
  type = input<string>(this._config.type);

  get isKeysAllowed(): boolean {
    return this._isKeysAllowed;
  }

  set isKeysAllowed(value: boolean) {
    this._isKeysAllowed = value;
  }

  @HostBinding('class.tab-container') clazz = true;

  tabs: TabDirective[] = [];
  classMap: { [key: string]: boolean } = {};

  /** aria label for tab list */
  ariaLabel = 'Tabs';

  protected isDestroyed = false;
  protected _isKeysAllowed = true;
  private defaultActivationScheduled = false;

  constructor(
    private _config: TabsetConfig,
    private renderer: Renderer2,
    private elementRef: ElementRef
  ) {
    this._isKeysAllowed = _config.isKeysAllowed;
    this.ariaLabel = _config.ariaLabel;

    // Watch for input changes and update class map
    effect(() => {
      const _ = [this.vertical(), this.justified(), this.type()];
      this.setClassMap();
    });
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
  }

  addTab(tab: TabDirective): void {
    // If tab has a tabOrder, insert it in the correct position
    if (tab.tabOrder !== undefined) {
      this.insertTabByOrder(tab);
    } else {
      // Default behavior - add to end
      this.tabs.push(tab);
    }

    // Activation logic
    // - If the newly added tab is already active, set it active again to leverage
    //   TabDirective's setter which will deactivate others.
    // - Otherwise, schedule a single deferred default-first activation if none active.
    if (tab.active) {
      tab.active = true;
      return;
    }

    if (!this.defaultActivationScheduled) {
      this.defaultActivationScheduled = true;
      // Defer default activation to avoid racing template-driven [active] inputs
      Promise.resolve().then(() => {
        this.defaultActivationScheduled = false;
        // Guard in case the tabset changed meanwhile
        if (!this.tabs.length) {
          return;
        }
        if (this.tabs.some((t: TabDirective) => !!t.active)) {
          return;
        }
        const firstEnabled = this.tabs.find((t: TabDirective) => !t.disabled);
        if (firstEnabled) {
          firstEnabled.active = true;
        }
      });
    }
  }

  private insertTabByOrder(tab: TabDirective): void {
    let insertIndex = this.tabs.length; // Default to end

    // Find the correct position to insert the ordered tab
    for (let i = 0; i < this.tabs.length; i++) {
      const existingTab = this.tabs[i];

      // If the existing tab has an order and the new tab's order is less than it
      if (existingTab.tabOrder !== undefined &&
          tab.tabOrder !== undefined &&
          tab.tabOrder < existingTab.tabOrder) {
        insertIndex = i;
        break;
      }

      // If we reach an unordered tab, we want to insert before it
      // (ordered tabs should come before unordered tabs)
      if (existingTab.tabOrder === undefined) {
        insertIndex = i;
        break;
      }
    }

    // Insert at the found position
    this.tabs.splice(insertIndex, 0, tab);
  }

  removeTab(
    tab: TabDirective,
    options = { reselect: true, emit: true }
  ): void {
    const index = this.tabs.indexOf(tab);
    if (index === -1 || this.isDestroyed) {
      return;
    }
    // Select a new tab if the tab to be removed is selected and not destroyed
    if (options.reselect && tab.active && this.hasAvailableTabs(index)) {
      const newActiveIndex = this.getClosestTabIndex(index);
      this.tabs[newActiveIndex].active = true;
    }
    if (options.emit) {
      tab.removed.emit(tab);
    }
    this.tabs.splice(index, 1);
    if (tab.elementRef.nativeElement.parentNode) {
      this.renderer.removeChild(
        tab.elementRef.nativeElement.parentNode,
        tab.elementRef.nativeElement
      );
    }
  }

    keyNavActions(event: KeyboardEvent, index: number) {
    if (!this.isKeysAllowed) {
      return;
    }
    const list: HTMLElement[] = Array.from(this.elementRef.nativeElement.querySelectorAll('.nav-link'));
    // const activeElList = list.filter((el: HTMLElement) => !el.classList.contains('disabled'));
    if (event.keyCode === 13 || event.key === 'Enter' || event.keyCode === 32 || event.key === 'Space') {
      event.preventDefault();
      const currentTab = list[(index) % list.length];
      currentTab.click();

      return;
    }

    if (event.keyCode === 39 || event.key === 'RightArrow') {
      let nextTab: HTMLElement;
      let shift = 1;

      do {
        nextTab = list[(index + shift) % list.length];

        shift++;
      } while (nextTab.classList.contains('disabled'));

      nextTab.focus();

      return;
    }

    if (event.keyCode === 37 || event.key === 'LeftArrow') {
      let previousTab: HTMLElement;
      let shift = 1;
      let i = index;

      do {
        if ((i - shift) < 0) {
          i = list.length - 1;
          previousTab = list[i];
          shift = 0;
        } else {
          previousTab = list[i - shift];
        }

        shift++;
      } while (previousTab.classList.contains('disabled'));

      previousTab.focus();

      return;
    }

    if (event.keyCode === 36 || event.key === 'Home') {
      event.preventDefault();

      let firstTab: HTMLElement;
      let shift = 0;

      do {
        firstTab = list[shift % list.length];

        shift++;
      } while (firstTab.classList.contains('disabled'));

      firstTab.focus();

      return;
    }

    if (event.keyCode === 35 || event.key === 'End') {
      event.preventDefault();

      let lastTab: HTMLElement;
      let shift = 1;
      let i = index;

      do {
        if ((i - shift) < 0) {
          i = list.length - 1;
          lastTab = list[i];
          shift = 0;
        } else {
          lastTab = list[i - shift];
        }

        shift++;
      } while (lastTab.classList.contains('disabled'));

      lastTab.focus();

      return;
    }

    if (event.keyCode === 46 || event.key === 'Delete') {
      if (this.tabs[index].removable) {
        this.removeTab(this.tabs[index]);

        if (list[index + 1]) {
          list[(index + 1) % list.length].focus();

          return;
        }

        if (list[list.length - 1]) {
          list[0].focus();
        }
      }
    }
  }

  protected getClosestTabIndex(index: number): number {
    const tabsLength = this.tabs.length;
    if (!tabsLength) {
      return -1;
    }

    for (let step = 1; step <= tabsLength; step += 1) {
      const prevIndex = index - step;
      const nextIndex = index + step;
      if (this.tabs[prevIndex] && !this.tabs[prevIndex].disabled) {
        return prevIndex;
      }
      if (this.tabs[nextIndex] && !this.tabs[nextIndex].disabled) {
        return nextIndex;
      }
    }

    return -1;
  }

  protected hasAvailableTabs(index: number): boolean {
    const tabsLength = this.tabs.length;
    if (!tabsLength) {
      return false;
    }

    for (let i = 0; i < tabsLength; i += 1) {
      if (!this.tabs[i].disabled && i !== index) {
        return true;
      }
    }

    return false;
  }

  protected setClassMap(): void {
    this.classMap = {
      'nav-stacked': this.vertical(),
      'flex-column': this.vertical(),
      'nav-justified': this.justified(),
      [`nav-${this.type()}`]: true
    };
  }
}
